import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import cashboxService from '../services/cashboxService';
import { Upload, X, Check, AlertCircle, FileText, DollarSign, Calendar, Wallet, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';

const CashboxInjection = ({ onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState('manual');
  const [transactions, setTransactions] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Buscar categorias do caixa
  const { data: categoriesData, isLoading: loadingCategories } = useQuery({
    queryKey: ['cashbox-categories'],
    queryFn: () => cashboxService.getCategories(),
  });

  const categories = categoriesData?.data?.categories || categoriesData?.data || [];

  // Mutation para criar transação
  const createTransaction = useMutation({
    mutationFn: (data) => cashboxService.createTransaction(data),
  });

  // Adicionar linha manual
  const addManualLine = () => {
    setTransactions([...transactions, { amount: '', reference_date: '', description: '', category_code: 'OUTRAS_RECEITAS', payment_method: 'cash' }]);
  };

  // Atualizar linha
  const updateLine = (index, field, value) => {
    const newTransactions = [...transactions];
    newTransactions[index][field] = value;
    setTransactions(newTransactions);
  };

  // Remover linha
  const removeLine = (index) => {
    setTransactions(transactions.filter((_, i) => i !== index));
  };

  // Processar paste (formato: valor|data|descricao|categoria|pagamento)
  const handlePaste = () => {
    const text = textareaRef.current?.value || '';
    if (!text.trim()) return;

    const lines = text.split('\n').filter(line => line.trim());
    const parsed = [];

    for (const line of lines) {
      const parts = line.split('|').map(p => p.trim());
      if (parts.length >= 2) {
        parsed.push({
          amount: parseFloat(parts[0].replace(/[R$ .\-,]/g, '')) || 0,
          reference_date: parts[1] || new Date().toISOString().split('T')[0],
          description: parts[2] || 'Injeção de saldo - caixa empresa',
          category_code: parts[3] || 'OUTRAS_RECEITAS',
          payment_method: parts[4] || 'cash',
        });
      }
    }

    setTransactions(parsed);
    setMode('manual');
    toast.success(`${parsed.length} linhas processadas`);
  };

  // Importar arquivo CSV - CORRIGIDO: parsing robusto para vírgulas em campos e formato brasileiro
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result || '';
      const lines = text.split('\n').filter(line => line.trim());
      const parsed = [];

      const startIndex = lines[0]?.toLowerCase().includes('valor') ? 1 : 0;

      for (let i = startIndex; i < lines.length; i++) {
        // Parsing CSV robusto: lidar com campos entre aspas contendo vírgulas
        const parts = parseCSVLine(lines[i]);
        if (parts.length >= 2) {
          const rawAmount = parts[0] || '0';
          // Parsear número brasileiro: 1.234,56 ou 1234,56 ou 1234.56
          const amount = parseBrazilianNumber(rawAmount);

          parsed.push({
            amount,
            reference_date: parts[1] || new Date().toISOString().split('T')[0],
            description: parts[2] || 'Injeção de saldo - caixa empresa',
            category_code: parts[3] || 'OUTRAS_RECEITAS',
            payment_method: parts[4] || 'cash',
          });
        }
      }

      setTransactions(parsed);
      toast.success(`${parsed.length} registros do arquivo`);
    };
    reader.readAsText(file);
  };

  // Função auxiliar para parsing CSV com campos entre aspas
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  // Função auxiliar para parsear números no formato brasileiro
  const parseBrazilianNumber = (str) => {
    if (!str) return 0;
    // Remove R$, espaços, etc
    const cleaned = str.replace(/[R$\s]/g, '');
    // Se tem vírgula como separador decimal (formato brasileiro: 1.234,56)
    if (cleaned.includes(',') && cleaned.includes('.')) {
      // 1.234,56 -> 1234.56
      return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
    } else if (cleaned.includes(',') && !cleaned.includes('.')) {
      // 1234,56 -> 1234.56
      return parseFloat(cleaned.replace(',', '.')) || 0;
    } else {
      // 1234.56 ou 1234
      return parseFloat(cleaned) || 0;
    }
  };

  // Validar dados
  const isValid = () => {
    return transactions.length > 0 && transactions.every(t => {
      const amount = parseFloat(t.amount);
      return !isNaN(amount) && amount > 0 && t.reference_date && t.description;
    });
  };

  // Calcular total
  const totalAmount = transactions.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

  // Importar todas as transações
  const handleImport = async () => {
    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (const t of transactions) {
      try {
        const amount = parseFloat(t.amount);
        if (isNaN(amount) || amount <= 0) {
          errorCount++;
          errors.push(`Linha ${successCount + errorCount}: Valor inválido`);
          continue;
        }

        await createTransaction.mutateAsync({
          transaction_type: 'INCOME',
          category_code: t.category_code,
          description: t.description,
          amount: amount,
          reference_date: t.reference_date,
          payment_method: t.payment_method,
        });
        successCount++;
      } catch (error) {
        errorCount++;
        const msg = error.response?.data?.message || 'Erro desconhecido';
        errors.push(`Linha ${successCount + errorCount}: ${msg}`);
        console.error('Erro ao criar transação:', error);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} entrada(s) registrada(s) no caixa!`);
      queryClient.invalidateQueries(['cashbox-stats']);
      queryClient.invalidateQueries(['cashbox-transactions']);
    }
    if (errorCount > 0) {
      setImportErrors(errors);
      toast.error(`${errorCount} erro(s) na importação`);
    } else {
      setImportErrors([]);
    }

    onSuccess?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-emerald-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Wallet className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Injeção de Saldo no Caixa da Empresa</h2>
              <p className="text-sm text-gray-600">Registre valores históricos de caixa (sem projeto)</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-amber-50 border-b border-amber-100">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-800">Atenção: Injeção de Caixa Livre</p>
              <p className="text-amber-700">
                Estes valores entrarão diretamente no <strong>Caixa da Empresa</strong> (não atrelados a projetos).
                Use para registrar saldos de anos anteriores ou caixa físico histórico.
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setMode('manual')}
            className={`px-4 py-3 text-sm font-medium ${mode === 'manual' ? 'border-b-2 border-emerald-500 text-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Preencher Manualmente
          </button>
          <button
            onClick={() => setMode('paste')}
            className={`px-4 py-3 text-sm font-medium ${mode === 'paste' ? 'border-b-2 border-emerald-500 text-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Colar de Planilha
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {mode === 'manual' ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600">{transactions.length} registros</span>
                <button
                  onClick={addManualLine}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100"
                >
                  <Check className="w-4 h-4" />
                  Adicionar Linha
                </button>
              </div>

              {transactions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Nenhum registro adicionado</p>
                  <p className="text-sm">Clique em "Adicionar Linha" para começar</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((trans, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-5 gap-2">
                        <input
                          type="number"
                          placeholder="Valor (R$)"
                          value={trans.amount}
                          onChange={(e) => updateLine(index, 'amount', e.target.value)}
                          className="px-2 py-1.5 text-sm border rounded"
                          min="0"
                          step="0.01"
                        />
                        <input
                          type="date"
                          value={trans.reference_date}
                          onChange={(e) => updateLine(index, 'reference_date', e.target.value)}
                          className="px-2 py-1.5 text-sm border rounded"
                        />
                        <input
                          type="text"
                          placeholder="Descrição"
                          value={trans.description}
                          onChange={(e) => updateLine(index, 'description', e.target.value)}
                          className="px-2 py-1.5 text-sm border rounded col-span-2"
                        />
                        <select
                          value={trans.category_code}
                          onChange={(e) => updateLine(index, 'category_code', e.target.value)}
                          className="px-2 py-1.5 text-sm border rounded"
                          disabled={loadingCategories || !Array.isArray(categories)}
                        >
                          {loadingCategories ? (
                            <option value="">Carregando...</option>
                          ) : categories && categories.filter ? (
                            categories.filter(c => c.type === 'INCOME').map(cat => (
                              <option key={cat.code} value={cat.code}>{cat.name}</option>
                            ))
                          ) : (
                            <option value="OUTRAS_RECEITAS">Outras Receitas</option>
                          )}
                        </select>
                      </div>
                      <button
                        onClick={() => removeLine(index)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {transactions.length > 0 && importErrors.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-red-800">Erros na importação:</span>
                  </div>
                  <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                    {importErrors.map((err, i) => (
                      <li key={i}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {transactions.length > 0 && (
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium text-emerald-800">Total: R$ {totalAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <span className="text-sm text-emerald-600">
                    Será adicionado ao caixa da empresa
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-medium text-blue-800 mb-2">Formato aceito:</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• <strong>Colar de Excel:</strong> valor | data | descrição | categoria | método</li>
                  <li>• <strong>Exemplo:</strong> 25000.00|2025-01-15|Saldo caixa histórico|OUTRAS_RECEITAS|cash</li>
                </ul>
              </div>

              <textarea
                ref={textareaRef}
                placeholder="Cole aqui os dados da planilha (uma linha por registro)&#10;Exemplo:&#10;25000.00|2025-01-15|Saldo janeiro|cash&#10;15000.50|2025-02-01|Caixa histórico|transfer"
                className="w-full h-64 p-3 border rounded-lg font-mono text-sm"
              />

              <button
                onClick={handlePaste}
                className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <FileText className="w-4 h-4" />
                Processar Dados Colados
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept=".csv,.txt"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-100"
            >
              <Upload className="w-4 h-4" />
              Importar CSV
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancelar
            </button>
            <button
              onClick={handleImport}
              disabled={!isValid() || createTransaction.isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {createTransaction.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Injetar R$ {totalAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashboxInjection;